import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useHttp } from "../hooks/useHttp";

const SOCIAL_LINKS = {
  INSTAGRAM: "https://www.instagram.com/ilovecatgame/",
  TIKTOK: "https://www.tiktok.com/@ilovecatgame?lang=en",
};

interface ClaimedChip {
  chipUid: string;
  toyType: number;
  emailAddress: string;
  claimedAt: string;
  redeemUrl: string | null;
}

export const CatGame = () => {
  const navigate = useNavigate();
  const { sendRequest } = useHttp();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimedChips, setClaimedChips] = useState<ClaimedChip[]>([]);
  const [isLoadingChips, setIsLoadingChips] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);

  const loadClaimedChips = async () => {
    setIsLoadingChips(true);
    try {
      const { data } = await sendRequest<{
        success: boolean;
        chips: ClaimedChip[];
      }>({
        endpoint: '/catgame/my-chips',
        method: 'GET',
        responseType: 'json',
      });

      if (data.success) {
        setClaimedChips(data.chips);
      }
    } catch (err) {
      console.error('Failed to load claimed chips:', err);
    } finally {
      setIsLoadingChips(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await sendRequest<{
          authenticated: boolean;
          email?: string;
        }>({
          endpoint: '/catgame/session',
          method: 'GET',
          responseType: 'json',
        });

        setIsAuthenticated(data.authenticated);
        if (!data.authenticated) {
          setShowLoginModal(true);
        } else {
          // Load claimed chips if authenticated
          loadClaimedChips();
        }
      } catch (err) {
        setIsAuthenticated(false);
        setShowLoginModal(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [sendRequest]);

  const handleLogin = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await sendRequest<{
        success: boolean;
        message: string;
      }>({
        endpoint: '/catgame/login',
        method: 'POST',
        payload: { email },
        responseType: 'json',
      });

      if (data.success) {
        setIsAuthenticated(true);
        setShowLoginModal(false);
        // Notify other components that authentication status has changed
        window.dispatchEvent(new CustomEvent('authStatusChanged'));
        // Load claimed chips after successful login
        loadClaimedChips();
      } else {
        setError(data.message || "Failed to login");
      }
    } catch (err: any) {
      setError(err instanceof Error ? err.message : "Failed to login");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100dvh-149.5px)] p-6">
        <div className="flex justify-center items-center w-16 h-16 bg-blue-100 rounded-full animate-spin">
          <svg
            className="w-8 h-8 text-blue-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (showLoginModal && !isAuthenticated) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />

            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Login
            </h1>

            <p className="mb-8 text-gray-600">
              Enter your email to access your CatGame rewards and collectibles.
            </p>

            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleLogin();
                  }
                }}
              />
            </div>

            {error && (
              <div className="px-4 py-3 mb-4 text-sm text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}

            <button
              onClick={handleLogin}
              disabled={!email || isSubmitting}
              className="w-full py-3 font-bold text-white bg-[#1F1F1F] rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Logging in..." : "Login"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated content
  return (
    <div className="p-6 space-y-5">
      <div className="border border-black rounded-[14px] p-3.5 w-full">
        <div className="flex justify-between items-center">
          <h6 className="font-bold leading-none">My 223 Portal</h6>

          <div className="flex items-center gap-1.5">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.3333 9.33333C11.5101 9.33333 11.6797 9.2631 11.8047 9.13807C11.9298 9.01305 12 8.84348 12 8.66667C12 8.48986 11.9298 8.32029 11.8047 8.19526C11.6797 8.07024 11.5101 8 11.3333 8C11.1565 8 10.987 8.07024 10.8619 8.19526C10.7369 8.32029 10.6667 8.48986 10.6667 8.66667C10.6667 8.84348 10.7369 9.01305 10.8619 9.13807C10.987 9.2631 11.1565 9.33333 11.3333 9.33333ZM11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333C12 11.1565 11.9298 10.987 11.8047 10.8619C11.6797 10.7369 11.5101 10.6667 11.3333 10.6667C11.1565 10.6667 10.987 10.7369 10.8619 10.8619C10.7369 10.987 10.6667 11.1565 10.6667 11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12ZM8.66667 8.66667C8.66667 8.84348 8.59643 9.01305 8.4714 9.13807C8.34638 9.2631 8.17681 9.33333 8 9.33333C7.82319 9.33333 7.65362 9.2631 7.5286 9.13807C7.40357 9.01305 7.33333 8.84348 7.33333 8.66667C7.33333 8.48986 7.40357 8.32029 7.5286 8.19526C7.65362 8.07024 7.82319 8 8 8C8.17681 8 8.34638 8.07024 8.4714 8.19526C8.59643 8.32029 8.66667 8.48986 8.66667 8.66667ZM8.66667 11.3333C8.66667 11.5101 8.59643 11.6797 8.4714 11.8047C8.34638 11.9298 8.17681 12 8 12C7.82319 12 7.65362 11.9298 7.5286 11.8047C7.40357 11.6797 7.33333 11.5101 7.33333 11.3333C7.33333 11.1565 7.40357 10.987 7.5286 10.8619C7.65362 10.7369 7.82319 10.6667 8 10.6667C8.17681 10.6667 8.34638 10.7369 8.4714 10.8619C8.59643 10.987 8.66667 11.1565 8.66667 11.3333ZM4.66667 9.33333C4.84348 9.33333 5.01305 9.2631 5.13807 9.13807C5.2631 9.01305 5.33333 8.84348 5.33333 8.66667C5.33333 8.48986 5.2631 8.32029 5.13807 8.19526C5.01305 8.07024 4.84348 8 4.66667 8C4.48986 8 4.32029 8.07024 4.19526 8.19526C4.07024 8.32029 4 8.48986 4 8.66667C4 8.84348 4.07024 9.01305 4.19526 9.13807C4.32029 9.2631 4.48986 9.33333 4.66667 9.33333ZM4.66667 12C4.84348 12 5.01305 11.9298 5.13807 11.8047C5.2631 11.6797 5.33333 11.5101 5.33333 11.3333C5.33333 11.1565 5.2631 10.987 5.13807 10.8619C5.01305 10.7369 4.84348 10.6667 4.66667 10.6667C4.48986 10.6667 4.32029 10.7369 4.19526 10.8619C4.07024 10.987 4 11.1565 4 11.3333C4 11.5101 4.07024 11.6797 4.19526 11.8047C4.32029 11.9298 4.48986 12 4.66667 12Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.66665 1.16667C4.79926 1.16667 4.92644 1.21935 5.0202 1.31312C5.11397 1.40689 5.16665 1.53406 5.16665 1.66667V2.17534C5.60798 2.16667 6.09398 2.16667 6.62865 2.16667H9.37065C9.90598 2.16667 10.392 2.16667 10.8333 2.17534V1.66667C10.8333 1.53406 10.886 1.40689 10.9798 1.31312C11.0735 1.21935 11.2007 1.16667 11.3333 1.16667C11.4659 1.16667 11.5931 1.21935 11.6869 1.31312C11.7806 1.40689 11.8333 1.53406 11.8333 1.66667V2.21801C12.0067 2.23134 12.1709 2.24823 12.326 2.26867C13.1073 2.37401 13.74 2.59534 14.2393 3.094C14.738 3.59334 14.9593 4.22601 15.0647 5.00734C15.1667 5.76734 15.1667 6.73734 15.1667 7.96267V9.37067C15.1667 10.596 15.1667 11.5667 15.0647 12.326C14.9593 13.1073 14.738 13.74 14.2393 14.2393C13.74 14.738 13.1073 14.9593 12.326 15.0647C11.566 15.1667 10.596 15.1667 9.37065 15.1667H6.62998C5.40465 15.1667 4.43398 15.1667 3.67465 15.0647C2.89332 14.9593 2.26065 14.738 1.76132 14.2393C1.26265 13.74 1.04132 13.1073 0.935984 12.326C0.833984 11.566 0.833984 10.596 0.833984 9.37067V7.96267C0.833984 6.73734 0.833984 5.76667 0.935984 5.00734C1.04132 4.22601 1.26265 3.59334 1.76132 3.094C2.26065 2.59534 2.89332 2.37401 3.67465 2.26867C3.83021 2.24823 3.99443 2.23134 4.16732 2.21801V1.66667C4.16732 1.53418 4.2199 1.4071 4.31353 1.31335C4.40715 1.2196 4.53416 1.16685 4.66665 1.16667ZM3.80665 3.26C3.13665 3.35001 2.74998 3.51934 2.46798 3.80134C2.18598 4.08334 2.01665 4.47001 1.92665 5.14001C1.91154 5.25334 1.89865 5.37312 1.88798 5.49934H14.112C14.1013 5.37312 14.0884 5.25312 14.0733 5.13934C13.9833 4.46934 13.814 4.08267 13.532 3.80067C13.25 3.51867 12.8633 3.34934 12.1927 3.25934C11.508 3.16734 10.6047 3.16601 9.33332 3.16601H6.66665C5.39532 3.16601 4.49265 3.168 3.80665 3.26ZM1.83332 8.00001C1.83332 7.43067 1.83332 6.93534 1.84198 6.50001H14.158C14.1667 6.93534 14.1667 7.43067 14.1667 8.00001V9.33334C14.1667 10.6047 14.1653 11.508 14.0733 12.1933C13.9833 12.8633 13.814 13.25 13.532 13.532C13.25 13.814 12.8633 13.9833 12.1927 14.0733C11.508 14.1653 10.6047 14.1667 9.33332 14.1667H6.66665C5.39532 14.1667 4.49265 14.1653 3.80665 14.0733C3.13665 13.9833 2.74998 13.814 2.46798 13.532C2.18598 13.25 2.01665 12.8633 1.92665 12.1927C1.83465 11.508 1.83332 10.6047 1.83332 9.33334V8.00001Z"
                fill="black"
              />
            </svg>

            <span className="text-xs">
              {new Date().toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </span>
          </div>
        </div>

        {isLoadingChips ? (
          <div className="flex justify-center items-center py-8">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
            <p className="ml-2 text-gray-600">
              Loading your CatGame Collectibles...
            </p>
          </div>
        ) : claimedChips.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-600">
              You don't have any claimed CatGame toys yet.
            </p>
          </div>
        ) : (
          <div className="mt-4">
            {claimedChips.length === 1 && (
              <div className="relative rounded-[14px] overflow-hidden aspect-square">
                <img
                  src={`/images/catgame/toys/${claimedChips[0].toyType}.png`}
                  alt="CatGame Toy"
                  className="object-cover w-full h-full"
                />
              </div>
            )}

            {claimedChips.length === 2 && (
              <div className="grid grid-cols-2 gap-3">
                {claimedChips.map((chip) => (
                  <div
                    key={chip.chipUid}
                    className="relative rounded-[14px] overflow-hidden aspect-square"
                  >
                    <img
                      src={`/images/catgame/toys/${chip.toyType}.png`}
                      alt="CatGame Toy"
                      className="object-cover w-full h-full"
                    />
                  </div>
                ))}
              </div>
            )}

            {claimedChips.length === 3 && (
              <div className="grid grid-cols-2 gap-3">
                <div className="relative rounded-[14px] overflow-hidden aspect-auto">
                  <img
                    src={`/images/catgame/toys/${claimedChips[0].toyType}.png`}
                    alt="CatGame Toy"
                    className="object-cover w-full h-full"
                  />
                </div>

                <div className="flex flex-col gap-3">
                  {claimedChips.slice(1, 3).map((chip) => (
                    <div
                      key={chip.chipUid}
                      className="relative rounded-[14px] overflow-hidden aspect-[4/3]"
                    >
                      <img
                        src={`/images/catgame/toys/${chip.toyType}.png`}
                        alt="CatGame Toy"
                        className="object-cover w-full h-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {claimedChips.length >= 4 && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {(showAllChips ? claimedChips : claimedChips.slice(0, 4)).map((chip) => (
                    <div
                      key={chip.chipUid}
                      className="relative rounded-[14px] overflow-hidden aspect-square"
                    >
                      <img
                        src={`/images/catgame/toys/${chip.toyType}.png`}
                        alt="CatGame Toy"
                        className="object-cover w-full h-full"
                      />
                    </div>
                  ))}
                </div>

                {claimedChips.length > 4 && (
                  <div className="flex justify-center">
                    <button
                      onClick={() => setShowAllChips(!showAllChips)}
                      className="px-6 py-2 text-sm font-semibold text-black bg-white border-2 border-black rounded-[50px] hover:bg-black hover:text-white transition-colors"
                    >
                      {showAllChips
                        ? `Show Less`
                        : `Show All (${claimedChips.length})`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="bg-gradient-to-r from-[#116ACE] to-[#0353C2] p-4 rounded-[10px] px-[29.5px] py-3.5 font-bold sm:text-lg text-base text-white cursor-pointer text-center"
        onClick={() => navigate("/catgame-wallpaper")}
      >
        Download your CatGame wallpapers
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div
          className="relative rounded-[10px] overflow-hidden aspect-square cursor-pointer"
          onClick={() =>
            window.open(SOCIAL_LINKS.INSTAGRAM, "_blank", "noopener,noreferrer")
          }
        >
          <img
            src="/images/catgame/catgame-insta.png"
            alt="CatGame Instagram"
            className="object-cover h-full"
          />
        </div>

        <div
          className="relative rounded-[10px] overflow-hidden aspect-square cursor-pointer"
          onClick={() =>
            window.open(SOCIAL_LINKS.TIKTOK, "_blank", "noopener,noreferrer")
          }
        >
          <img
            src="/images/catgame/catgame-tiktok.png"
            alt="CatGame TikTok"
            className="object-cover h-full"
          />
        </div>
      </div>

      {/* <div className="h-[151px] w-full rounded-[15px] p-3 w-full flex flex-col justify-between relative overflow-hidden">
        <img src="/images/catgame/fish-bg.png" alt="" className="absolute inset-0" />

        <div className="max-w-[51px] relative">
          <img src="/images/catgame/catgame-logo.png" alt="" />
        </div>

        <div className="flex items-center justify-between relative z-10">
          <div className="text-lg text-black font-bold">Play TikTakMino!</div>


          <button
            className="text-sm font-bold bg-[#343434] text-white h-[30px] min-w-[112px] px-3 rounded-[50px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Play
          </button>
        </div>
      </div> */}

      <div>
        <div className="relative aspect-square">
          <img
            src="/images/catgame/ingame-reward.png"
            alt="In-game Reward"
            className="object-cover rounded-[15px] h-full"
          />

          <div className="flex absolute right-0 left-0 bottom-4 justify-center">
            <button
              onClick={() => navigate("/catgame-reward")}
              className="text-sm font-bold bg-[#343434] text-white py-2 px-3 rounded-[50px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Claim In-game Reward
            </button>
          </div>
        </div>

        <div className="flex gap-1.5 mt-2.5">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-[3px]">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M0 9C0 4.02923 4.02923 0 9 0C13.9708 0 18 4.02923 18 9C18 13.9708 13.9708 18 9 18C4.02923 18 0 13.9708 0 9ZM9 5.53846C9.18361 5.53846 9.3597 5.6114 9.48954 5.74123C9.61937 5.87107 9.69231 6.04716 9.69231 6.23077V9.69231C9.69231 9.87592 9.61937 10.052 9.48954 10.1818C9.3597 10.3117 9.18361 10.3846 9 10.3846C8.81639 10.3846 8.6403 10.3117 8.51046 10.1818C8.38063 10.052 8.30769 9.87592 8.30769 9.69231V6.23077C8.30769 6.04716 8.38063 5.87107 8.51046 5.74123C8.6403 5.6114 8.81639 5.53846 9 5.53846ZM9 13.1538C9.18361 13.1538 9.3597 13.0809 9.48954 12.9511C9.61937 12.8212 9.69231 12.6452 9.69231 12.4615C9.69231 12.2779 9.61937 12.1018 9.48954 11.972C9.3597 11.8422 9.18361 11.7692 9 11.7692C8.81639 11.7692 8.6403 11.8422 8.51046 11.972C8.38063 12.1018 8.30769 12.2779 8.30769 12.4615C8.30769 12.6452 8.38063 12.8212 8.51046 12.9511C8.6403 13.0809 8.81639 13.1538 9 13.1538Z" fill="black" />
          </svg>

          <p className="text-sm text-[#626262]">
            Please make sure to fully close the game before redeeming your items.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CatGame;
